import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabaseClient'
import { localDateKey } from './gameLogic'

// Subscribe to the partner's row for a given day. Emits status updates
// in three buckets so the UI can stay coy about guess counts:
//
//   { row: null,                       status: 'not_started' }
//   { row: { won: null,    guesses },  status: 'playing'     }
//   { row: { won: true,    guesses },  status: 'won'         }
//   { row: { won: false,   guesses },  status: 'lost'        }
//
// The realtime channel is set up before the initial fetch so we never
// miss an INSERT/UPDATE that fires in the gap between the two. Filter
// is server-side (user_id=eq.<partnerId>) so we don't waste a payload
// on every Wordle row in the table.
export function usePartnerWordleAttempt(partnerId, day) {
  const [row, setRow] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!partnerId || !day) {
      setRow(null)
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)

    const channel = supabase
      .channel(`wordle-partner-${partnerId}-${day}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'games_wordle_attempts',
          filter: `user_id=eq.${partnerId}`,
        },
        (payload) => {
          if (!alive) return
          const next = payload.new
          // The filter only narrows by user, not day — ignore other
          // days' rows (a midnight crossover, the partner playing a
          // backfill, etc.).
          if (!next || next.day !== day) return
          setRow(next)
        },
      )
      .subscribe()

    supabase
      .from('games_wordle_attempts')
      .select('guesses, won')
      .eq('user_id', partnerId)
      .eq('day', day)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return
        setRow(data || null)
        setLoading(false)
      })

    return () => {
      alive = false
      supabase.removeChannel(channel)
    }
  }, [partnerId, day])

  const status = !row
    ? 'not_started'
    : row.won === true
      ? 'won'
      : row.won === false
        ? 'lost'
        : 'playing'

  return { row, status, loading }
}

// Joint streak — consecutive days where BOTH partners won. Walks back
// from today (or yesterday if today's joint win isn't on the board
// yet) and stops at the first day either side missed. Mirrors the
// solo-streak logic in gameLogic.js so the two counts feel parallel.
//
// `refreshKey` is an arbitrary trigger — bump it whenever an event
// could've changed the joint window (self status flip, partner status
// flip). The hook re-fetches on every change so we don't have to
// poll. Cap at 120 rows (60 days × 2 players) to keep the payload
// small while still covering any realistic streak length.
export function useJointStreak(myUserId, partnerId, refreshKey) {
  const [streak, setStreak] = useState(0)

  useEffect(() => {
    if (!myUserId || !partnerId) {
      setStreak(0)
      return undefined
    }
    let alive = true

    supabase
      .from('games_wordle_attempts')
      .select('user_id, day, won')
      .in('user_id', [myUserId, partnerId])
      .eq('won', true)
      .order('day', { ascending: false })
      .limit(120)
      .then(({ data }) => {
        if (!alive) return
        const myWins = new Set()
        const partnerWins = new Set()
        for (const r of data || []) {
          if (r.user_id === myUserId) myWins.add(r.day)
          else if (r.user_id === partnerId) partnerWins.add(r.day)
        }
        let s = 0
        const cursor = new Date()
        // If today isn't a joint win yet, the streak we're counting is
        // the one carried in from yesterday — same logic as the solo
        // streak's "today-in-progress doesn't break it" rule.
        const todayKey = localDateKey(cursor)
        if (!(myWins.has(todayKey) && partnerWins.has(todayKey))) {
          cursor.setDate(cursor.getDate() - 1)
        }
        while (true) {
          const k = localDateKey(cursor)
          if (myWins.has(k) && partnerWins.has(k)) {
            s += 1
            cursor.setDate(cursor.getDate() - 1)
          } else {
            break
          }
        }
        setStreak(s)
      })

    return () => { alive = false }
  }, [myUserId, partnerId, refreshKey])

  return streak
}
